"use client";

import UserAvatar from "@/components/Avatar/UserAvatar";

/**
 * GroupedAvatar - Displays multiple participant avatars inside a circle
 */
export default function GroupedAvatar({
    participants = [],
    currentUserId,
    size = 56,
    className = "",
}) {
    const count = participants.length;

    // Single participant - show simple avatar
    if (count <= 1) {
        const participant = participants[0];
        return (
            <div
                className={`rounded-full overflow-hidden bg-gray-200 dark:bg-bgDark-300 ${className}`}
                style={{ width: size, height: size }}
            >
                {participant?.user?.photo && (
                    <UserAvatar
                        user={participant.user}
                        alt="avatar"
                        width={size}
                        height={size}
                        className="object-cover w-full h-full"
                    />
                )}
            </div>
        );
    }

    // Multiple participants
    const showPlusIndicator = count > 3;
    const avatarsToShow = showPlusIndicator ? 3 : count;
    const displayParticipants = participants.slice(0, avatarsToShow);
    const remainingCount = count - avatarsToShow;

    // Small avatar size - 45% of container to fit inside circle
    const smallSize = Math.round(size * 0.45);

    // Calculate positions to keep circles fully inside the main circle
    // Distance from center where small circle center can be placed
    const maxDistance = (size - smallSize) / 2 - 2; // -2 for ring spacing

    // Convert angle to position (angle in degrees, 0 = right, 90 = bottom)
    const angleToPosition = (angleDeg) => {
        const angleRad = (angleDeg * Math.PI) / 180;
        const centerX = size / 2 + maxDistance * Math.cos(angleRad);
        const centerY = size / 2 + maxDistance * Math.sin(angleRad);
        return {
            left: centerX - smallSize / 2,
            top: centerY - smallSize / 2,
        };
    };

    // Get positions based on total elements to display (avatars + plus indicator if any)
    const getPositions = (numAvatars, hasPlus) => {
        const total = hasPlus ? numAvatars + 1 : numAvatars;

        if (total === 2) {
            // Diagonal: top-left, bottom-right
            return [
                angleToPosition(-135), // top-left
                angleToPosition(45),   // bottom-right
            ];
        }

        if (total === 3) {
            // Triangle équilatéral: 120° entre chaque
            return [
                angleToPosition(-150), // top-left
                angleToPosition(-30),  // top-right
                angleToPosition(90),   // bottom-center
            ];
        }

        // 4 elements: diamond shape (top, right, bottom, left)
        return [
            angleToPosition(-90),  // top
            angleToPosition(0),    // right
            angleToPosition(180),  // left
            angleToPosition(90),   // bottom
        ];
    };

    const positions = getPositions(avatarsToShow, showPlusIndicator);

    return (
        <div
            className={`relative rounded-full bg-gray-200 dark:bg-bgDark-300 ${className}`}
            style={{ width: size, height: size }}
        >
            {displayParticipants.map((participant, index) => (
                <div
                    key={participant.id || index}
                    className="absolute rounded-full overflow-hidden ring-[2px] ring-gray-200 dark:ring-bgDark-300"
                    style={{
                        width: smallSize,
                        height: smallSize,
                        ...positions[index],
                    }}
                >
                    {participant.user?.photo && (
                        <UserAvatar
                            user={participant.user}
                            alt="avatar"
                            width={smallSize}
                            height={smallSize}
                            className="object-cover w-full h-full"
                        />
                    )}
                </div>
            ))}

            {/* +N indicator */}
            {showPlusIndicator && (
                <div
                    className="absolute rounded-full bg-gray-400 dark:bg-bgDark-400 flex items-center justify-center ring-[2px] ring-gray-200 dark:ring-bgDark-300"
                    style={{
                        width: smallSize,
                        height: smallSize,
                        ...positions[avatarsToShow],
                    }}
                >
                    <span
                        className="font-semibold text-white"
                        style={{ fontSize: smallSize * 0.40 }}
                    >
                        +{remainingCount}
                    </span>
                </div>
            )}
        </div>
    );
}
